import { Banner, BannerStatus, Button } from "@/components/DesignSystem";
import { QueryKeys } from "@/constants/QueryKeys";
import { useDashboard } from "@/hooks/useDashboard";
import { Salesperson } from "@/pages/CustomerDashboard/interfaces";
import {
  PointsExpirationViewAllDialog,
} from "@/pages/CustomerDashboard/PointsExpirationViewAllDialog";
import { SellerModal } from "@/pages/CustomerDashboard/WalletPage";
import { DashboardApi } from "@/utilities/api/DashboardApi";
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { UserApi } from "@/utilities/api/UserApi";
import { ApiUser } from "@api/interfaces";
import { ApiRewardsProgram } from "@api/interfaces/ApiRewardsProgram";
import { SxProps } from "@mui/material";
import {
  businessToday,
  daysFromDate,
  daysFromNow,
  formatBusinessDate,
  getJsDateFromString,
  groupBy,
  groupByDaysUntilExpiration,
} from '@shared/utilities';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";
import { useQuery } from "react-query";

export interface WalletBalance {
  balance: number,
  expirationDate: string | null,
  id: string,
  name: string,
  promotionId: string | null,
  rewardsProgramId: string | null,
  subwalletId: string,
}

const getProgramText = (rewardsProgram: ApiRewardsProgram, t: TFunction) => {
  const startFullYear = getJsDateFromString(rewardsProgram.startDate).getFullYear();
  const endYearShort = getJsDateFromString(rewardsProgram.endDate).getFullYear() % 100;
  return t(
    $ => $.customerDashboard.pointsExpiration.banner.programPeriod,
    { startYear: startFullYear, endYear: endYearShort },
  );
};

const getTitle = (
  walletBalances: WalletBalance[],
  t: TFunction,
  formatWithExpiringPoints = false,
) => {
  const walletBalancesGroupedByExpiration = groupBy(
    walletBalances, wallet => wallet.expirationDate ?? "3000-01-01",
  );
  const earliestExpirationDate = Object.keys(walletBalancesGroupedByExpiration).sort()[0];
  const pointsExpirationDate = getJsDateFromString(earliestExpirationDate, { endOfDay: true });
  const daysUntilExpiration = Math.floor(-daysFromDate(pointsExpirationDate));

  const earliestExpiringWalletBalances = walletBalancesGroupedByExpiration[earliestExpirationDate];
  const allExpiringToday = earliestExpiringWalletBalances.every(
    wallet => wallet.expirationDate === businessToday(),
  );

  let timeframeKey: 'expiringToday' | 'expiringTomorrow' | 'expiringInDays';
  let timeframeParams: { count?: number } = {};
  if (allExpiringToday || daysUntilExpiration === 0) {
    timeframeKey = 'expiringToday';
  } else if (daysUntilExpiration === 1) {
    timeframeKey = 'expiringTomorrow';
  } else {
    timeframeKey = 'expiringInDays';
    timeframeParams = { count: daysUntilExpiration };
  }

  if (formatWithExpiringPoints) {
    const totalPointsExpiring = earliestExpiringWalletBalances.reduce(
      (sum, wallet) => sum + wallet.balance, 0,
    );
    return t($ => $.customerDashboard.pointsExpiration.banner.unusedPointsExpiring, {
      count: totalPointsExpiring,
      timeframe: t(
        $ => $.customerDashboard.pointsExpiration.banner[timeframeKey],
        timeframeParams,
      ),
    });
  }

  return t($ => $.customerDashboard.pointsExpiration.banner.pointsExpiring, {
    timeframe: t(
      $ => $.customerDashboard.pointsExpiration.banner[timeframeKey],
      timeframeParams,
    ),
  });
};

interface ExpirationBanner {
  status: BannerStatus,
  getTitle: (wallets: WalletBalance[], t: TFunction) => string,
  getSubtitles: (
    wallets: WalletBalance[], t: TFunction, rewardsProgram?: ApiRewardsProgram,
  ) => string[],
}

const ExpirationBanners: Record<7 | 30 | 60 | 90, ExpirationBanner> = {
  7: {
    status: 'error',
    getTitle: (walletBalances: WalletBalance[], t: TFunction) => {
      const totalPointsExpiring = walletBalances.reduce(
        (sum, wallet) => sum + (Number(wallet.balance) || 0), 0,
      );
      const todayString = businessToday();
      const threeDaysFromNowString = formatBusinessDate(daysFromNow(3));
      const allExpiringToday = walletBalances.every(wallet => (
        wallet.expirationDate === todayString
      ));
      const allExpiringIn3Days = !allExpiringToday && walletBalances.every(
        wallet => wallet.expirationDate
          && wallet.expirationDate > todayString
          && wallet.expirationDate <= threeDaysFromNowString,
      );
      const timeframeKey = allExpiringToday ? 'expiringToday' : 'expiringInDays';
      let timeframeParams: { count?: number } = {};
      if (allExpiringIn3Days) {
        timeframeParams = { count: 3 };
      } else {
        timeframeParams = { count: 7 };
      }

      return t($ => $.customerDashboard.pointsExpiration.banner.unusedPointsExpiring, {
        count: totalPointsExpiring,
        timeframe: t(
          $ => $.customerDashboard.pointsExpiration.banner[timeframeKey],
          timeframeParams,
        ),
      });
    },
    getSubtitles: (
      walletBalances: WalletBalance[],
      t: TFunction,
      rewardsProgram?: ApiRewardsProgram,
    ) => {
      const todayString = businessToday();
      const threeDaysFromNowString = formatBusinessDate(daysFromNow(3));

      let pointsExpiringToday = 0;
      let pointsExpiringIn3Days = 0;

      for (const walletBalance of walletBalances) {
        if (!walletBalance.expirationDate) {
          continue;
        }
        if (walletBalance.expirationDate === todayString) {
          pointsExpiringToday += Number(walletBalance.balance) || 0;
        } else if (walletBalance.expirationDate <= threeDaysFromNowString) {
          pointsExpiringIn3Days += Number(walletBalance.balance) || 0;
        }
      }

      const allExpiringToday = walletBalances.every(wallet => (
        wallet.expirationDate === todayString
      ));
      if (allExpiringToday) {
        return [];
      }

      const allExpiringIn3Days = walletBalances.every(
        wallet => wallet.expirationDate
          && wallet.expirationDate > todayString
          && wallet.expirationDate <= threeDaysFromNowString,
      );
      if (allExpiringIn3Days) {
        return [];
      }

      const messages: string[] = [];
      if (pointsExpiringToday > 0) {
        messages.push(
          t($ => $.customerDashboard.pointsExpiration.banner.subtitle, {
            points: pointsExpiringToday,
          }),
        );
      }
      if (pointsExpiringIn3Days > 0) {
        messages.push(
          t(
            $ => $.customerDashboard.pointsExpiration.banner.subtitleDays,
            { points: pointsExpiringIn3Days },
          ),
        );
      }
      if (rewardsProgram) {
        messages.push(getProgramText(rewardsProgram, t));
      }
      return messages;
    },
  },
  30: {
    status: 'warning',
    getTitle: (walletBalances: WalletBalance[], t: TFunction) => getTitle(walletBalances, t),
    getSubtitles: (
      walletBalances: WalletBalance[],
      t: TFunction,
      rewardsProgram?: ApiRewardsProgram,
    ) => {
      if (!rewardsProgram || !walletBalances.some(wallet => wallet.promotionId === null)) {
        return [];
      }
      return [getProgramText(rewardsProgram, t)];
    },
  },
  60: {
    status: 'info',
    getTitle: (walletBalances: WalletBalance[], t: TFunction) => getTitle(walletBalances, t),
    getSubtitles: (
      walletBalances: WalletBalance[],
      t: TFunction,
      rewardsProgram?: ApiRewardsProgram,
    ) => {
      if (
        !rewardsProgram
        || !walletBalances.some(walletBalance => (
          walletBalance.promotionId === null
        ))
      ) {
        return [];
      }
      return [getProgramText(rewardsProgram, t)];
    },
  },
  90: {
    status: 'info',
    getTitle: (walletBalances: WalletBalance[], t: TFunction) => getTitle(walletBalances, t),
    getSubtitles: (
      walletBalances: WalletBalance[],
      t: TFunction,
      rewardsProgram?: ApiRewardsProgram,
    ) => {
      if (!rewardsProgram || !walletBalances.some(wallet => wallet.promotionId === null)) {
        return [];
      }
      return [getProgramText(rewardsProgram, t)];
    },
  },
} as const;

type ExpirationBannerId = keyof typeof ExpirationBanners;

const getExpirationBanner = (id: ExpirationBannerId): ExpirationBanner => {
  return ExpirationBanners[id];
};

interface PointsExpirationBannerProps {
  accessToken?: string,
  customer: ApiUser,
  onNavigateToWallet?: () => void,
  sx?: SxProps,
}

export const PointsExpirationBanner = ({
  accessToken,
  customer,
  onNavigateToWallet,
  sx,
}: PointsExpirationBannerProps) => {
  const { t } = useTranslation();
  const [showContactSellerDialog, setShowContactSellerDialog] = useState(false);
  const [showViewAllDialog, setShowViewAllDialog] = useState(false);

  const { data: dashboardData } = useDashboard(accessToken);

  const { data: userSubwalletBalances } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, customer.id],
    () => UserApi.getSubwalletBalances(customer.id),
    { enabled: !accessToken },
  );

  const { data: salespersons, isFetching: isSalespersonsFetching } = useQuery(
    [QueryKeys.GET_DASHBOARD_SALESPERSONS, accessToken],
    async () => await DashboardApi.listRelevantSalespersons(accessToken!),
    { enabled: !!accessToken },
  );

  const { data: rewardsPrograms } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, customer.retailerId],
    async () => {
      return await RewardsProgramApi.list({ retailerId: customer.retailerId! });
    },
    { enabled: !!customer.retailerId && !accessToken },
  );

  const subwalletBalances = userSubwalletBalances ?? dashboardData?.balances;

  const rawWalletBalances = subwalletBalances
    ?.filter(balance => balance.points !== 0 && !balance.bookingId)
    .map(balance => ({
      ...balance,
      balance: balance.points,
      name: balance.subwallet.name,
    }));

  if (!rawWalletBalances || !rawWalletBalances.length) {
    return null;
  }

  const programs = (
    dashboardData?.programs ?? rewardsPrograms
  ) as ApiRewardsProgram[] | undefined;

  const walletGroups = groupByDaysUntilExpiration<WalletBalance>(rawWalletBalances);

  const expiringGroupIds = Object.keys(walletGroups)
    .map(Number)
    .filter(groupId => groupId in ExpirationBanners);
  if (!expiringGroupIds.length) {
    return null;
  }
  const expiringSoonestGroupId = Math.min(...expiringGroupIds) as ExpirationBannerId;
  const bannerToDisplay = getExpirationBanner(expiringSoonestGroupId);

  if (!bannerToDisplay) {
    return null;
  }

  const expiringWallets = walletGroups[expiringSoonestGroupId];
  const rewardsProgramWallet = expiringWallets.find(
    wallet => !!wallet.expirationDate && !wallet.promotionId,
  );
  const previousRewardsProgram = programs?.find(
    program => program.id === rewardsProgramWallet?.rewardsProgramId,
  );

  return (
    <>
      <Banner
        actionButtons={[
          (accessToken && bannerToDisplay.status === 'error') && (
            <Button
              disabled={isSalespersonsFetching || !salespersons?.length}
              key="contact-seller-button"
              onClick={() => setShowContactSellerDialog(true)}
              testID="contact-seller-button"
            >
              {t($ => $.customerDashboard.pointsExpiration.banner.contactSeller)}
            </Button>
          ),
          <Button
            key="view-all-button"
            onClick={() => setShowViewAllDialog(true)}
            testID="view-all-button"
          >
            {t($ => $.customerDashboard.pointsExpiration.banner.viewAll)}
          </Button>,
        ]}
        icon={(
          <MaterialSymbol
            fill
            icon="alarm"
          />
        )}
        messages={[...bannerToDisplay.getSubtitles(expiringWallets, t, previousRewardsProgram)]}
        stackMessages
        status={bannerToDisplay.status}
        sx={sx}
        title={bannerToDisplay.getTitle(expiringWallets, t)}
      />
      {showContactSellerDialog && !!salespersons && (
        <SellerModal
          onClose={() => setShowContactSellerDialog(false)}
          salespersons={salespersons as Salesperson[]}
        />
      )}
      {showViewAllDialog && (
        <PointsExpirationViewAllDialog
          onClose={() => setShowViewAllDialog(false)}
          onNavigateToWallet={onNavigateToWallet && (() => {
            setShowViewAllDialog(false);
            onNavigateToWallet();
          })}
          walletBalances={rawWalletBalances}
        />
      )}
    </>
  );
};
